import { Grid, useTheme } from "@mui/material";
import React, { useState } from "react";
import CommonFormField from "../../common/components/CommonFormField";
import CommonSelect from "../../common/components/CommonSelect";
import CommonCard from "../../common/components/CommonCard";
import { Academics, ActivitiesAt, ActivitiesStatus } from "../../common/const";
import CommonButton from "../../common/components/CommonButton";
import { Add } from "@mui/icons-material";
import LocationSearch from "../../common/components/LocationSearch";
import { setLoading } from "../../features/authSlice";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { createActivity } from "../../api/activityApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";

const CurricularInfo = ({
    formData,
    setFormData
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { loading, user } = useSelector((state) => state.auth);
    const [errors, setErrors] = useState({});
    
    const validateField = (name, value) => {
        switch (name) {
            case 'activityName':
                if (!value) return 'Activity Name is required';
                return '';
            case 'startdate':
                if (!value) return 'Start Date is required';
                return '';
            case 'status':
                if (!value) return 'Status is required';
                return '';
            case 'noofhours':
                if (!value && value !== 0) return 'Hours are required';
                if (value < 0) return 'Hours cannot be negative';
                if (value > 10000) return 'Hours cannot exceed 10000';
                if (value === 0) return 'Hours must be greater than 0';
                return '';
            default:
                return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Special handling for noofhours to prevent exceeding max
        if (name === 'noofhours') {
            const numValue = Number(value);
            if (numValue > 10000) {
                setErrors(prev => ({ 
                    ...prev, 
                    noofhours: 'Hours cannot exceed 10000' 
                }));
                return;
            }
            if (numValue < 0) {
                setErrors(prev => ({ 
                    ...prev, 
                    noofhours: 'Hours cannot be negative' 
                }));
                return;
            }
        }
        
        setFormData((prev) => ({
            ...prev,
            curricular: {
                ...prev.curricular,
                [name]: value,
            },
        }));

        // Clear error for the field being changed
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData.curricular).forEach(key => {
            const error = validateField(key, formData.curricular[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            
            // Additional hours validation before submission
            const hours = Number(formData.curricular.noofhours);
            if (hours > 10000) {
                showNotification('Contributed hours cannot exceed 10000', 'error');
                return;
            }
            if (hours < 0) {
                showNotification('Contributed hours cannot be negative', 'error');
                return;
            }
            if (hours === 0) {
                showNotification('Contributed hours must be greater than 0', 'error');
                return;
            }
            
            dispatch(setLoading({
                loading: true
            }));
            const response = await createActivity({
                "activity_name": formData.curricular.activityName,
                "activity_location": ActivitiesAt.find((a) => a.value === formData.curricular.activityAt).label?.toUpperCase(), //SCHOOL, OUTSIDE
                "google_location": formData.curricular.activityLocation !== '' ? formData.curricular.activityLocation : null,
                "contribution_hours": formData.curricular.noofhours,
                "status": formData.curricular.status,
                "activity_status": formData.curricular.activityStatus,
                "start_date": formData.curricular.startdate !== '' ? new Date(formData.curricular.startdate).toISOString() : null,
                "end_date": formData.curricular.enddate !== '' ? new Date(formData.curricular.enddate).toISOString() : null,
                "student_grade": localStorage.getItem('current_grade'),
                "student_id": user.student_id
            });
            if (response.data.extra_curricular_activity_id) {
                showNotification('Extra curricular activity added successfully', 'success');
                dispatch(setLoading({
                    loading: false
                }));
                setFormData((prev) => ({
                    ...prev,
                    curricular: {
                        activityAt: ActivitiesAt[0].value,
                        activityName: '',
                        noofhours: '',
                        status: '',
                        startdate: '',
                        enddate: '',
                        activityStatus: '',
                        activityLocation: '',
                    },
                    activityCliked: true
                }));
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            dispatch(setLoading({
                loading: false
            }));
            showNotification(errorMessage, 'error');
        }
    }

    const getMinDate = () => {
        const date = formData.curricular.startdate ? new Date(formData.curricular.startdate) : new Date();
        date.setDate(date.getDate() + 1); // Add 7 days
        return date.toISOString().split('T')[0];
    };
    
    return (
        <Grid container spacing={1.5} mt={0.5} mb={2}>
            {loading && (
                <LoadingSpinner />
            )}
            <Grid item xs={12}>
                <CommonCard
                    marginTop={0.5}
                    borderRadius={1}
                    border={`1px solid ${theme.palette.background.light}`}
                    content={
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} md={12}>
                                <CommonSelect
                                    label="Activities At"
                                    name="activityAt"
                                    value={formData.curricular.activityAt}
                                    onChange={handleInputChange}
                                    placeholder="Select Activities At"
                                    options={ActivitiesAt}
                                />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <CommonFormField
                                    label="Activity Name"
                                    type="email"
                                    name="activityName"
                                    value={formData.curricular.activityName}
                                    onChange={handleInputChange}
                                    error={errors.activityName}
                                    placeholder="e.g., Team Captain"
                                />
                            </Grid>
                            {formData.curricular.activityAt === 2 && (
                                <Grid item xs={12} md={12}>
                                    <LocationSearch
                                        label="Location"
                                        value={formData.curricular.activityLocation}
                                        onChange={(selectedLocation) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                curricular: {
                                                    ...prev.curricular,
                                                    activityLocation: selectedLocation
                                                }
                                            }));
                                        }}
                                        placeholder="Select Location"
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="Contributed Number of Hours/Week"
                                    type="number"
                                    name="noofhours"
                                    value={formData.curricular.noofhours}
                                    onChange={handleInputChange}
                                    placeholder="Enter Hours (Max 10000)"
                                    error={errors.noofhours}
                                    inputProps={{
                                        min: 1,
                                        max: 10000,
                                        step: 1
                                    }}
                                    onKeyDown={(e) => {
                                        // Prevent typing 'e', 'E', '-', and other invalid characters
                                        if (e.key === 'e' || e.key === 'E' || e.key === '-' || e.key === '+' || e.key === '.') {
                                            e.preventDefault();
                                        }
                                    }}
                                    onInput={(e) => {
                                        // Additional validation: prevent values > 10000 while typing
                                        const value = parseInt(e.target.value);
                                        if (value > 10000) {
                                            e.target.value = 10000;
                                            handleInputChange({ target: { name: 'noofhours', value: 10000 } });
                                        }
                                        if (value < 0) {
                                            e.target.value = 1;
                                            handleInputChange({ target: { name: 'noofhours', value: 1 } });
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonSelect
                                    label="Status"
                                    name="status"
                                    value={formData.curricular.status}
                                    onChange={handleInputChange}
                                    placeholder="Select Status"
                                    error={errors.status}
                                    options={Academics}
                                />
                            </Grid>
                            <Grid item xs={12} md={formData.curricular.status === 'PAST' ? 6 : 12}>
                                <CommonFormField
                                    label="Start Date"
                                    type="date"
                                    name="startdate"
                                    value={formData.curricular.startdate}
                                    onChange={handleInputChange}
                                    error={errors.startdate}
                                    placeholder="Select Start Date"
                                />
                            </Grid>
                            {formData.curricular.status === 'PAST' && (
                                <Grid item xs={12} md={6}>
                                    <CommonFormField
                                        label="End Date"
                                        type="date"
                                        name="enddate"
                                        value={formData.curricular.enddate}
                                        onChange={handleInputChange}
                                        placeholder="Select End Date"
                                        minDate={getMinDate()}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} md={12}>
                                <CommonSelect
                                    label="Activity Status"
                                    name="activityStatus"
                                    value={formData.curricular.activityStatus}
                                    onChange={handleInputChange}
                                    placeholder="Completed/ In Progress"
                                    options={ActivitiesStatus}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <CommonButton
                                    label="Add Activity"
                                    background={theme.palette.secondary.main}
                                    color={theme.palette.background.main}
                                    fontWeight={600}
                                    handleClick={() => handleSubmit()}
                                    startIcon={<Add />}
                                    marginBottom={1}
                                />
                            </Grid>
                        </Grid>
                    }
                    paddingBottom={0}
                    padding="16px 16px 0px 16px"
                />
            </Grid>
        </Grid>
    )
}

export default CurricularInfo;